import { ComponentProps, useMemo, useRef, useState } from 'react'

import { combineRefs } from '@cash-web/shared-feature-form'
import { Modal, ModalPane } from '@cash-web/shared-feature-modal'
import { FlowContextProps } from '@cash-web/shared-flow-data-access-context'
import { JourneyData, JourneyRoute, JourneyScope, JourneyTarget } from '@cash-web/shared-journey-data-access-context'

import { getTargetHistory } from './getTargetHistory'
import { useJourneyFlowInputFocus } from './hooks/useJourneyFlowInputFocus'
import { useJourneyScrollPosition } from './hooks/useJourneyScrollPosition'
import { JourneyMoneyBotModal } from './JourneyMoneyBotModal'
import { JourneyPane } from './JourneyPaneRenderer'
import { JourneyRouter } from './JourneyRouter'

export type JourneyTargetRendererProps = {
  history: JourneyData[]
  onBack?: () => void
  onClose: () => void
  onCompleteFlow: () => void
  onCancelFlow: () => void
  target: JourneyTarget
} & ComponentProps<typeof JourneyRouter>
export const JourneyTargetRenderer = ({ history, onBack, onClose, target, ...props }: JourneyTargetRendererProps) => {
  const targetHistory = useMemo(() => getTargetHistory(history)[target], [history, target])
  /**
   * Stores the active flow reference for each target
   * This is used to cancel the flow when the user clicks the back button`
   * or the close button
   */
  const activeFlowRef = useRef<FlowContextProps | null>(null)
  const [activeBlocker, setActiveBlocker] = useState<FlowContextProps['active'] | null>(null)

  const { ref: focusRef } = useJourneyFlowInputFocus({ isActive: true, flow: activeFlowRef.current })
  const { ref: scrollRef } = useJourneyScrollPosition(targetHistory.at(-1)?.journeyId)

  const ref = combineRefs(focusRef, scrollRef) as React.Ref<HTMLDivElement>

  if (targetHistory.length === 0) {
    return null
  }
  const panes = targetHistory.map(journey => {
    return (
      <JourneyPane
        key={journey.journeyId}
        {...props}
        isActive={targetHistory.at(-1)?.journeyId === journey.journeyId}
        journeyData={journey}
        setActiveFlowRef={(flow: FlowContextProps) => {
          activeFlowRef.current = flow
          // we should be updating when we change blocker views
          if (
            activeBlocker?.blockerData?.id !== flow.active?.blockerData?.id ||
            activeBlocker?.blockerData?.type !== flow.active?.blockerData?.type
          ) {
            setActiveBlocker(flow.active)
          }
        }}
      />
    )
  })

  const active = targetHistory[targetHistory.length - 1]

  const activeRouteScopeConfig = active.opts?.scope as JourneyRoute & JourneyScope

  /**
   * Captures the focused element when a flow is changing blocker views and when a journey is active and the first element is focused
   */

  const handleCancelActionWithFlow = (cancelAction?: () => void) => {
    const flow = activeFlowRef.current
    if (flow?.isInProgress) {
      // this overrides the default cancel flow behavior, and waits for any dismiss action to be triggered and then calls the cancelAction
      // if the user actually cancelled the flow, we call the cancelAction
      flow.cancel({
        onCancelFlowOverride: () => {
          cancelAction?.()
        },
      })
    } else {
      cancelAction?.()
    }
  }

  const handleClose = () => {
    handleCancelActionWithFlow?.(onClose)
  }

  const getBackHandler = () => {
    const flow = activeFlowRef.current
    if (flow?.isInProgress && flow.active?.blockerData?.id === 'FLOW_TERMINATION') {
      return undefined
    }
    if (targetHistory.length === 1 && target === 'modal') {
      return undefined
    }
    return () => handleCancelActionWithFlow?.(onBack)
  }

  const handleBack = getBackHandler()

  switch (target) {
    case 'modal':
      return (
        <Modal
          ref={ref}
          variant={activeRouteScopeConfig?.modalVariant ?? 'default'}
          onClose={handleClose}
          onBack={handleBack}
          disableAnimation={activeRouteScopeConfig.disableModalAnimation}
          overflowScroll={activeRouteScopeConfig.overflowScroll}
          dataTestId={active.path}
        >
          {panes}
        </Modal>
      )

    case 'inline':
      return <div ref={ref}>{panes}</div>

    case 'hidden':
      return <div style={{ display: 'none' }}>{panes}</div>

    case 'moneybot':
      return <JourneyMoneyBotModal onClose={handleClose}>{panes}</JourneyMoneyBotModal>

    case 'fullscreen':
    default:
      return (
        <ModalPane ref={ref} onBack={handleBack} onClose={handleClose} dataTestId={active.path}>
          {panes}
        </ModalPane>
      )
  }
}
